/**
 * Spirits taproom liquor orders: MLT- (Madison → OBB Taproom) and ALT-
 * (Athens → OBBA Taproom). Works like the beer taproom orders but uses only
 * /api/liquor — nothing here touches beer transfers, invoices, ABC beer, TTB
 * or QuickBooks. Bottles leave on hand as sold to the taproom at $0.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import '../App.css';
import { apiFetch } from '../utils/api';
import { API_BASE_URL } from '../config';
import { centralToday } from '../utils/centralDate';

const API = `${API_BASE_URL}/api/liquor/taproom-orders`;

const SITES = [
  { siteId: 'DSP-AL-20010', name: 'Madison Distillery', taproom: 'OBB Taproom', prefix: 'MLT' },
  { siteId: 'DSP-AL-20051', name: 'Athens Distillery', taproom: 'OBBA Taproom', prefix: 'ALT' },
];

const site = (siteId: string) => SITES.find((s) => s.siteId === siteId) || SITES[0];

const money = (n: number) => `$${(Number(n) || 0).toFixed(2)}`;

async function call<T>(url: string, init?: RequestInit): Promise<T> {
  const res = await apiFetch(url, init);
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error((body && body.error) || `HTTP ${res.status}`);
  return body as T;
}

const json = (method: string, body?: unknown): RequestInit => ({
  method,
  headers: { 'Content-Type': 'application/json' },
  body: body === undefined ? undefined : JSON.stringify(body),
});

function printPage() {
  document.body.classList.add('mic-printing');
  const cleanup = () => {
    document.body.classList.remove('mic-printing');
    window.removeEventListener('afterprint', cleanup);
  };
  window.addEventListener('afterprint', cleanup);
  window.print();
}

const PrintStyles: React.FC = () => (
  <style>{`
    @media print {
      body.mic-printing .no-print { display: none !important; }
      body.mic-printing .menu, body.mic-printing .navbar-mobile { display: none !important; }
      body.mic-printing .spirit-sheet { font-size: 12pt; }
    }
    .spirit-sign-line { border-bottom: 1px solid #333; min-height: 28px; }
  `}</style>
);

function useSite(): [string, (s: string) => void] {
  const [params, setParams] = useSearchParams();
  const siteId = params.get('siteId') || SITES[0].siteId;
  const setSite = (s: string) => {
    const next = new URLSearchParams(params);
    next.set('siteId', s);
    setParams(next, { replace: true });
  };
  return [siteId, setSite];
}

const SiteSelect: React.FC<{ siteId: string; onChange: (s: string) => void; id: string }> = ({ siteId, onChange, id }) => (
  <div className="flow-field">
    <label htmlFor={id}>Distillery</label>
    <select id={id} value={siteId} onChange={(e) => onChange(e.target.value)} data-testid={id}>
      {SITES.map((s) => (
        <option key={s.siteId} value={s.siteId}>
          {s.name} → {s.taproom}
        </option>
      ))}
    </select>
  </div>
);

type OrderLine = {
  id?: number;
  productCode: string;
  productName: string;
  bottleSizeMl: number;
  bottlesPerCase: number;
  labelingProof: number | null;
  cases: number;
  looseBottles: number;
  totalBottles: number;
  label: string;
  unitPrice: number;
  amount: number;
};

type Order = {
  id: number;
  ref: string;
  orderNumber: string | null;
  siteId: string;
  siteName: string;
  taproomName: string;
  orderDate: string;
  status: 'Draft' | 'Completed' | 'Voided';
  notes: string | null;
  voidReason: string | null;
  readOnly: boolean;
  totalAmount: number;
  totalBottles?: number;
  lineCount?: number;
  lines?: OrderLine[];
};

type Avail = {
  productCode: string;
  productName: string;
  bottleSizeMl: number;
  bottlesPerCase: number;
  onHandBottles: number;
  availableBottles: number;
  availableLabel: string;
};

const statusBadge = (s: string) => (
  <span className={`flow-badge ${s === 'Completed' ? 'complete' : ''}`} style={s === 'Voided' ? { background: '#ffe6e6', color: '#a00' } : undefined}>
    {s}
  </span>
);

// ---------------------------------------------------------------------------
// List
// ---------------------------------------------------------------------------

export const SpiritsTaproomOrderList: React.FC = () => {
  const [siteId, setSiteId] = useSite();
  const navigate = useNavigate();
  const [orders, setOrders] = useState<Order[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const s = site(siteId);

  const load = useCallback(async () => {
    try {
      setOrders(await call<Order[]>(`${API}?siteId=${encodeURIComponent(siteId)}`));
      setError(null);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to load orders');
    }
  }, [siteId]);

  useEffect(() => {
    void load();
  }, [load]);

  const newOrder = async () => {
    setBusy(true);
    try {
      const o = await call<Order>(API, json('POST', { siteId, orderDate: centralToday() }));
      navigate(`/spirits/taproom-orders/${o.id}`);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Could not start an order');
      setBusy(false);
    }
  };

  return (
    <div className="page-container container" data-testid="spirit-taproom-orders">
      <div className="flow-toolbar" style={{ marginBottom: 12 }}>
        <Link to="/spirits">← Spirits</Link>
        <Link to={`/spirits/bottles?siteId=${encodeURIComponent(siteId)}`}>Bottles on hand</Link>
        <Link to={`/spirits/taproom-report?siteId=${encodeURIComponent(siteId)}`} data-testid="spirit-taproom-to-report">
          Liquor report
        </Link>
      </div>
      <h2 className="app-header">Taproom liquor orders</h2>
      <p className="flow-hint">
        Bottles from the distillery to its taproom, sold at $0. {s.name} orders are numbered {s.prefix}- and go to {s.taproom}.
        Completing an order takes the bottles off on hand.
      </p>
      <div className="flow-field-grid" style={{ marginBottom: 16 }}>
        <SiteSelect siteId={siteId} onChange={setSiteId} id="spirit-taproom-site" />
      </div>
      <button type="button" className="flow-primary" onClick={() => void newOrder()} disabled={busy} data-testid="spirit-taproom-new" style={{ marginBottom: 16 }}>
        New order to {s.taproom}
      </button>
      {error && (
        <p className="error" role="alert">
          {error}
        </p>
      )}
      {orders.length === 0 ? (
        <p className="flow-hint">No orders yet.</p>
      ) : (
        <div className="flow-card-list">
          {orders.map((o) => (
            <button
              key={o.id}
              type="button"
              className="flow-card tappable"
              onClick={() => navigate(`/spirits/taproom-orders/${o.id}`)}
              data-testid={`spirit-taproom-card-${o.id}`}
            >
              <div className="flow-card-row">
                <p className="flow-card-title" style={{ margin: 0 }}>
                  {o.ref}
                </p>
                {statusBadge(o.status)}
              </div>
              <p className="flow-card-meta">
                {o.orderDate} · {o.taproomName} · {o.totalBottles || 0} bottles · {money(0)}
              </p>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Detail: draft editor, transfer sheet, void
// ---------------------------------------------------------------------------

type Qty = { cases: number; loose: number };

export const SpiritsTaproomOrderDetail: React.FC = () => {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState<Order | null>(null);
  const [avail, setAvail] = useState<Avail[]>([]);
  const [qty, setQty] = useState<Record<string, Qty>>({});
  const [orderDate, setOrderDate] = useState('');
  const [notes, setNotes] = useState('');
  const [dirty, setDirty] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [voidOpen, setVoidOpen] = useState(false);
  const [voidReason, setVoidReason] = useState('');

  const loadAvail = useCallback(async (siteId: string, date: string) => {
    const rows = await call<Avail[]>(`${API}/availability?siteId=${encodeURIComponent(siteId)}&orderDate=${encodeURIComponent(date)}`);
    setAvail(rows);
  }, []);

  const apply = useCallback(
    async (o: Order) => {
      setOrder(o);
      setOrderDate(o.orderDate);
      setNotes(o.notes || '');
      const q: Record<string, Qty> = {};
      for (const l of o.lines || []) q[l.productCode] = { cases: l.cases, loose: l.looseBottles };
      setQty(q);
      setDirty(false);
      if (o.status === 'Draft') await loadAvail(o.siteId, o.orderDate);
    },
    [loadAvail]
  );

  useEffect(() => {
    (async () => {
      try {
        await apply(await call<Order>(`${API}/${orderId}`));
      } catch (err: unknown) {
        setError(err instanceof Error ? err.message : 'Failed to load order');
      }
    })();
  }, [orderId, apply]);

  const availByCode = useMemo(() => new Map(avail.map((a) => [a.productCode, a])), [avail]);
  const back = order ? `/spirits/taproom-orders?siteId=${encodeURIComponent(order.siteId)}` : '/spirits/taproom-orders';

  const setLine = (a: Avail, cases: number, loose: number) => {
    const per = a.bottlesPerCase || 6;
    const total = Math.max(0, cases * per + loose);
    if (total > a.availableBottles) {
      setError(`Only ${a.availableLabel} of ${a.productName} on hand for ${orderDate}.`);
      return;
    }
    setError(null);
    setQty((prev) => ({ ...prev, [a.productCode]: { cases: Math.floor(total / per), loose: total % per } }));
    setDirty(true);
  };

  const linesPayload = () =>
    Object.entries(qty)
      .filter(([, v]) => v.cases > 0 || v.loose > 0)
      .map(([productCode, v]) => ({ productCode, cases: v.cases, looseBottles: v.loose }));

  const save = async (): Promise<Order | null> => {
    if (!order) return null;
    setSaving(true);
    try {
      const o = await call<Order>(`${API}/${order.id}`, json('PATCH', { orderDate, notes, lines: linesPayload() }));
      await apply(o);
      setError(null);
      return o;
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Save failed');
      return null;
    } finally {
      setSaving(false);
    }
  };

  const changeDate = async (d: string) => {
    setOrderDate(d);
    setDirty(true);
    if (order && /^\d{4}-\d{2}-\d{2}$/.test(d)) {
      try {
        await loadAvail(order.siteId, d);
      } catch (err: unknown) {
        setError(err instanceof Error ? err.message : 'Failed to load on hand');
      }
    }
  };

  const complete = async () => {
    if (!order) return;
    const total = linesPayload().reduce((s, l) => {
      const a = availByCode.get(l.productCode);
      return s + l.cases * (a?.bottlesPerCase || 6) + l.looseBottles;
    }, 0);
    if (!total) {
      setError('Add at least one product first.');
      return;
    }
    if (!window.confirm(`Complete this order? ${total} bottles leave on hand as sold to ${order.taproomName} at $0.`)) return;
    const saved = await save();
    if (!saved) return;
    setSaving(true);
    try {
      await apply(await call<Order>(`${API}/${order.id}/complete`, json('POST', {})));
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Complete failed');
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!order || !window.confirm('Delete this draft?')) return;
    try {
      await call(`${API}/${order.id}`, json('DELETE'));
      navigate(back);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Delete failed');
    }
  };

  const doVoid = async () => {
    if (!order) return;
    if (!voidReason.trim()) {
      setError('A reason is required to void.');
      return;
    }
    setSaving(true);
    try {
      await apply(await call<Order>(`${API}/${order.id}/void`, json('POST', { reason: voidReason.trim() })));
      setVoidOpen(false);
      setVoidReason('');
      setError(null);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Void failed');
    } finally {
      setSaving(false);
    }
  };

  if (!order && !error) return <div className="page-container container">Loading…</div>;
  if (!order) {
    return (
      <div className="page-container container">
        <p className="error">{error}</p>
        <Link to="/spirits/taproom-orders">Back</Link>
      </div>
    );
  }

  // ---- Draft editor -------------------------------------------------------
  if (order.status === 'Draft') {
    const shown = avail.filter((a) => a.availableBottles > 0 || qty[a.productCode]);
    const totalBottles = Object.entries(qty).reduce(
      (s, [code, v]) => s + v.cases * (availByCode.get(code)?.bottlesPerCase || 6) + v.loose,
      0
    );
    return (
      <div className="page-container container" data-testid="spirit-taproom-draft">
        <div className="flow-toolbar" style={{ marginBottom: 12 }}>
          <button type="button" onClick={() => navigate(back)}>
            Back
          </button>
        </div>
        <h2 className="app-header">
          {order.ref} · {order.siteName} → {order.taproomName}
        </h2>
        <p className="flow-hint">
          Pick products in cases and loose bottles (750 ml). You can't order more than is on hand for the order date.
          The {site(order.siteId).prefix}- number is given when you complete it.
        </p>
        <div className="flow-field-grid" style={{ marginBottom: 16 }}>
          <div className="flow-field">
            <label htmlFor="spirit-taproom-date">Order date</label>
            <input id="spirit-taproom-date" type="date" value={orderDate} onChange={(e) => void changeDate(e.target.value)} />
          </div>
          <div className="flow-field">
            <label htmlFor="spirit-taproom-notes">Notes</label>
            <input id="spirit-taproom-notes" value={notes} onChange={(e) => { setNotes(e.target.value); setDirty(true); }} />
          </div>
        </div>
        {error && (
          <p className="error" role="alert">
            {error}
          </p>
        )}
        {shown.length === 0 ? (
          <p className="flow-hint">No bottles on hand at {order.siteName} for this date.</p>
        ) : (
          <div className="flow-card-list" style={{ marginBottom: 16 }}>
            {shown.map((a) => {
              const q = qty[a.productCode] || { cases: 0, loose: 0 };
              const picked = q.cases > 0 || q.loose > 0;
              return (
                <div key={a.productCode} className={`flow-card ${picked ? 'selected' : ''}`} data-testid={`spirit-taproom-item-${a.productCode}`}>
                  <p className="flow-card-title">{a.productName}</p>
                  <p className="flow-card-meta">
                    On hand {a.availableLabel} · {a.bottleSizeMl} ml · {a.bottlesPerCase}/case
                  </p>
                  <div className="flow-card-row">
                    <span className="flow-card-meta">Cases</span>
                    <div className="flow-stepper">
                      <button type="button" aria-label="Fewer cases" onClick={() => setLine(a, q.cases - 1, q.loose)} disabled={q.cases <= 0}>
                        −
                      </button>
                      <span className="flow-stepper-value">{q.cases}</span>
                      <button type="button" aria-label="More cases" onClick={() => setLine(a, q.cases + 1, q.loose)}>
                        +
                      </button>
                    </div>
                  </div>
                  <div className="flow-card-row">
                    <span className="flow-card-meta">Bottles</span>
                    <div className="flow-stepper">
                      <button type="button" aria-label="Fewer bottles" onClick={() => setLine(a, q.cases, q.loose - 1)} disabled={q.cases <= 0 && q.loose <= 0}>
                        −
                      </button>
                      <span className="flow-stepper-value">{q.loose}</span>
                      <button type="button" aria-label="More bottles" onClick={() => setLine(a, q.cases, q.loose + 1)}>
                        +
                      </button>
                    </div>
                  </div>
                  {picked && (
                    <p className="flow-card-meta">
                      {q.cases * a.bottlesPerCase + q.loose} bottles ·{' '}
                      <button type="button" style={{ background: 'none', color: '#a00', padding: 0 }} onClick={() => setLine(a, 0, 0)}>
                        Remove
                      </button>
                    </p>
                  )}
                </div>
              );
            })}
          </div>
        )}
        <p className="flow-hint">
          Total {totalBottles} bottles · {money(0)}
        </p>
        <div className="flow-toolbar">
          <button type="button" onClick={() => void save()} disabled={saving || !dirty} data-testid="spirit-taproom-save">
            Save draft
          </button>
          <button type="button" className="flow-primary" onClick={() => void complete()} disabled={saving} data-testid="spirit-taproom-complete">
            Complete order
          </button>
          <button type="button" style={{ background: '#ffe6e6', color: '#a00' }} onClick={() => void remove()} disabled={saving}>
            Delete draft
          </button>
        </div>
      </div>
    );
  }

  // ---- Completed / voided: $0 transfer sheet ------------------------------
  const lines = order.lines || [];
  return (
    <div className="page-container container" data-testid="spirit-taproom-sheet">
      <div className="flow-toolbar no-print" style={{ marginBottom: 12 }}>
        <button type="button" onClick={() => navigate(back)}>
          Back
        </button>
        <button type="button" onClick={printPage} data-testid="spirit-taproom-print">
          Print
        </button>
        {order.status === 'Completed' && (
          <button type="button" style={{ background: '#ffe6e6', color: '#a00' }} onClick={() => setVoidOpen((v) => !v)} data-testid="spirit-taproom-void">
            Void
          </button>
        )}
      </div>
      {error && (
        <p className="error no-print" role="alert">
          {error}
        </p>
      )}
      {voidOpen && (
        <div className="flow-card no-print" style={{ marginBottom: 16 }}>
          <p className="flow-card-meta">Voiding puts the bottles back on hand. A reason is required.</p>
          <div className="flow-field">
            <label htmlFor="spirit-taproom-void-reason">Reason</label>
            <input id="spirit-taproom-void-reason" value={voidReason} onChange={(e) => setVoidReason(e.target.value)} />
          </div>
          <button type="button" style={{ background: '#ffe6e6', color: '#a00', marginTop: 8 }} onClick={() => void doVoid()} disabled={saving || !voidReason.trim()}>
            Void {order.ref}
          </button>
        </div>
      )}
      <article className="spirit-sheet">
        <h2 className="app-header">Taproom transfer · {order.ref}</h2>
        {order.status === 'Voided' && (
          <p className="error" style={{ fontWeight: 600 }}>
            VOIDED{order.voidReason ? ` — ${order.voidReason}` : ''}
          </p>
        )}
        <div className="flow-field-grid" style={{ marginBottom: 12 }}>
          <div className="flow-field">
            <label>Date</label>
            <div>{order.orderDate}</div>
          </div>
          <div className="flow-field">
            <label>Order</label>
            <div>{order.ref}</div>
          </div>
          <div className="flow-field">
            <label>From</label>
            <div>
              {order.siteName} ({order.siteId})
            </div>
          </div>
          <div className="flow-field">
            <label>To</label>
            <div>{order.taproomName}</div>
          </div>
        </div>
        <table className="ttb-report__table" style={{ width: '100%', fontSize: 14 }}>
          <thead>
            <tr>
              <th>Product</th>
              <th className="ttb-report__num">Proof</th>
              <th>Size</th>
              <th className="ttb-report__num">Cases + bottles</th>
              <th className="ttb-report__num">Bottles</th>
              <th className="ttb-report__num">Price</th>
              <th className="ttb-report__num">Amount</th>
            </tr>
          </thead>
          <tbody>
            {lines.map((l) => (
              <tr key={l.productCode}>
                <td>{l.productName}</td>
                <td className="ttb-report__num">{l.labelingProof ?? '—'}</td>
                <td>{l.bottleSizeMl} ml</td>
                <td className="ttb-report__num">{l.label}</td>
                <td className="ttb-report__num">{l.totalBottles}</td>
                <td className="ttb-report__num">{money(l.unitPrice)}</td>
                <td className="ttb-report__num">{money(l.amount)}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr>
              <th colSpan={4}>Total</th>
              <th className="ttb-report__num">{order.totalBottles || 0}</th>
              <th />
              <th className="ttb-report__num">{money(order.totalAmount)}</th>
            </tr>
          </tfoot>
        </table>
        {order.notes && <p style={{ marginTop: 12 }}>Notes: {order.notes}</p>}
        <div className="flow-field-grid" style={{ marginTop: 32 }}>
          <div className="flow-field">
            <label>Released by</label>
            <div className="spirit-sign-line" />
          </div>
          <div className="flow-field">
            <label>Received by</label>
            <div className="spirit-sign-line" />
          </div>
          <div className="flow-field">
            <label>Date</label>
            <div className="spirit-sign-line" />
          </div>
        </div>
      </article>
      <PrintStyles />
    </div>
  );
};

// ---------------------------------------------------------------------------
// Monthly report (Alabama liquor return)
// ---------------------------------------------------------------------------

type ReportRow = {
  productCode: string;
  productName: string;
  bottleSizeMl: number;
  bottlesPerCase: number;
  startBottles: number;
  bottled: number;
  soldToTaproom: number;
  countAdjust: number;
  endBottles: number;
  countedBottles: number | null;
  soldLabel: string;
  endLabel: string;
};

type Report = {
  siteId: string;
  siteName: string;
  taproomName: string;
  month: string;
  start: string;
  end: string;
  startAsOf?: string;
  endAsOf?: string;
  count: { id: number; ref: string; countType: string; countDate: string } | null;
  products: ReportRow[];
  totals: { startBottles: number; bottled: number; soldToTaproom: number; countAdjust: number; endBottles: number };
  orders: { id: number; orderNumber: string; orderDate: string; status: string; totalBottles: number; voidReason: string | null }[];
};

export const SpiritsTaproomReport: React.FC = () => {
  const [siteId, setSiteId] = useSite();
  const [params, setParams] = useSearchParams();
  const month = params.get('month') || centralToday().slice(0, 7);
  const [rep, setRep] = useState<Report | null>(null);
  const [error, setError] = useState<string | null>(null);

  const setMonth = (m: string) => {
    const next = new URLSearchParams(params);
    next.set('month', m);
    setParams(next, { replace: true });
  };

  useEffect(() => {
    (async () => {
      try {
        setRep(await call<Report>(`${API}/report?siteId=${encodeURIComponent(siteId)}&month=${encodeURIComponent(month)}`));
        setError(null);
      } catch (err: unknown) {
        setError(err instanceof Error ? err.message : 'Failed to load report');
      }
    })();
  }, [siteId, month]);

  return (
    <div className="page-container container" data-testid="spirit-taproom-report">
      <div className="flow-toolbar no-print" style={{ marginBottom: 12 }}>
        <Link to="/spirits">← Spirits</Link>
        <Link to={`/spirits/taproom-orders?siteId=${encodeURIComponent(siteId)}`}>Taproom orders</Link>
        <button type="button" onClick={printPage}>
          Print
        </button>
      </div>
      <h2 className="app-header">Liquor report · bottles sold to the taproom</h2>
      <p className="flow-hint no-print">
        For the Alabama monthly liquor return. Sold to taproom counts completed orders; voided orders net to zero.
      </p>
      <div className="flow-field-grid no-print" style={{ marginBottom: 16 }}>
        <SiteSelect siteId={siteId} onChange={setSiteId} id="spirit-report-site" />
        <div className="flow-field">
          <label htmlFor="spirit-report-month">Month</label>
          <input id="spirit-report-month" type="month" value={month} onChange={(e) => e.target.value && setMonth(e.target.value)} />
        </div>
      </div>
      {error && (
        <p className="error" role="alert">
          {error}
        </p>
      )}
      {rep && (
        <article className="spirit-sheet">
          <p>
            <strong>{rep.siteName}</strong> ({rep.siteId}) → <strong>{rep.taproomName}</strong> · {rep.start} to {rep.end}
            {rep.count ? ` · Count ${rep.count.ref} (${rep.count.countDate})` : ''}
          </p>
          {(rep.startAsOf || rep.endAsOf) && (
            <p className="flow-hint">
              Start = {rep.startAsOf}. End = {rep.endAsOf}. Each is before that day&apos;s orders and moves.
            </p>
          )}
          <table className="ttb-report__table" style={{ width: '100%', fontSize: 14 }}>
            <thead>
              <tr>
                <th>Product</th>
                <th>Size</th>
                <th className="ttb-report__num" title={rep.startAsOf}>Start</th>
                <th className="ttb-report__num">Bottled</th>
                <th className="ttb-report__num">Sold to taproom</th>
                <th className="ttb-report__num">Cases + bottles sold</th>
                <th className="ttb-report__num">Count adj.</th>
                <th className="ttb-report__num" title={rep.endAsOf}>End</th>
              </tr>
            </thead>
            <tbody>
              {rep.products.map((r) => (
                <tr key={`${r.productCode}:${r.bottleSizeMl}`}>
                  <td>{r.productName}</td>
                  <td>{r.bottleSizeMl} ml</td>
                  <td className="ttb-report__num">{r.startBottles}</td>
                  <td className="ttb-report__num">{r.bottled}</td>
                  <td className="ttb-report__num">{r.soldToTaproom}</td>
                  <td className="ttb-report__num">{r.soldLabel}</td>
                  <td className="ttb-report__num">{r.countAdjust}</td>
                  <td className="ttb-report__num">{r.endBottles}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr>
                <th colSpan={2}>Total bottles</th>
                <th className="ttb-report__num">{rep.totals.startBottles}</th>
                <th className="ttb-report__num">{rep.totals.bottled}</th>
                <th className="ttb-report__num">{rep.totals.soldToTaproom}</th>
                <th />
                <th className="ttb-report__num">{rep.totals.countAdjust}</th>
                <th className="ttb-report__num">{rep.totals.endBottles}</th>
              </tr>
            </tfoot>
          </table>
          <h3 className="h6" style={{ marginTop: 16 }}>
            Orders this month
          </h3>
          {rep.orders.length === 0 ? (
            <p className="flow-hint">None.</p>
          ) : (
            <table className="ttb-report__table" style={{ width: '100%', fontSize: 13 }}>
              <thead>
                <tr>
                  <th>Order</th>
                  <th>Date</th>
                  <th>Status</th>
                  <th className="ttb-report__num">Bottles</th>
                </tr>
              </thead>
              <tbody>
                {rep.orders.map((o) => (
                  <tr key={o.id}>
                    <td>
                      <Link to={`/spirits/taproom-orders/${o.id}`}>{o.orderNumber}</Link>
                    </td>
                    <td>{o.orderDate}</td>
                    <td>
                      {o.status}
                      {o.voidReason ? ` — ${o.voidReason}` : ''}
                    </td>
                    <td className="ttb-report__num">{o.totalBottles}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </article>
      )}
      <PrintStyles />
    </div>
  );
};

export default SpiritsTaproomOrderList;
